import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { Toaster } from "@/components/ui/toaster";
import { Layout } from "@/components/Layout";

// Lazy load pages for better performance
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const TemplateEditor = lazy(() => import("@/pages/TemplateEditor"));
const Settings = lazy(() => import("@/pages/Settings"));
const BatchReport = lazy(() => import("@/pages/BatchReport"));
const Batches = lazy(() => import("@/pages/Batches"));
const Login = lazy(() => import("@/pages/Login"));
const Users = lazy(() => import("@/pages/Users"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-klein mx-auto mb-3" />
        <p className="text-sm text-ink-500 font-medium">Loading...</p>
      </div>
    </div>
  );
}

// Protected route wrapper with layout
function Guard({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  
  return <Layout>{children}</Layout>;
}

function HomeRoute() {
  const { user, loading, isAdmin } = useAuth();

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/batch-report" replace />;

  return <Guard><Dashboard /></Guard>;
}

function AdminOnlyRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/batch-report" replace />;

  return <Guard>{children}</Guard>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<HomeRoute />} />
        <Route path="/template/:id" element={<Guard><TemplateEditor /></Guard>} />
        <Route path="/batch-report" element={<Guard><BatchReport /></Guard>} />
        <Route path="/batches" element={<Guard><Batches /></Guard>} />
        <Route path="/settings" element={<AdminOnlyRoute><Settings /></AdminOnlyRoute>} />
        <Route path="/users" element={<AdminOnlyRoute><Users /></AdminOnlyRoute>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
